// src/pages/CRM/SFA/sfaService.js

import axiosInstance from "../../../utils/axiosInstance";

// ==================== OPPORTUNITIES ====================

export const opportunityService = {
  // List opportunities
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/opportunities', { params });
    return response.data;
  },

  // Get pipeline view (for Kanban)
  getPipeline: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/opportunities/pipeline', { params });
    return response.data;
  },

  // Get single opportunity
  getById: async (id) => {
    const response = await axiosInstance.get(`/api/crm/opportunities/${id}`);
    return response.data;
  },

  // Create opportunity
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/opportunities', data);
    return response.data;
  },

  // Update opportunity
  update: async (id, data) => {
    const response = await axiosInstance.put(`/api/crm/opportunities/${id}`, data);
    return response.data;
  },

  // Update stage only (for drag-drop)
  updateStage: async (id, stageData) => {
    const response = await axiosInstance.put(`/api/crm/opportunities/${id}/stage`, stageData);
    return response.data;
  },

  // Delete opportunity
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/crm/opportunities/${id}`);
    return response.data;
  },

  // Team Members
  addTeamMember: async (opportunityId, data) => {
    const response = await axiosInstance.post(`/api/crm/opportunities/${opportunityId}/team`, data);
    return response.data;
  },

  updateTeamMember: async (opportunityId, memberId, data) => {
    const response = await axiosInstance.put(`/api/crm/opportunities/${opportunityId}/team/${memberId}`, data);
    return response.data;
  },

  removeTeamMember: async (opportunityId, memberId) => {
    const response = await axiosInstance.delete(`/api/crm/opportunities/${opportunityId}/team/${memberId}`);
    return response.data;
  },

  // Line Items (Products)
  addProduct: async (opportunityId, data) => {
    const response = await axiosInstance.post(`/api/crm/opportunities/${opportunityId}/products`, data);
    return response.data;
  },

  updateProduct: async (opportunityId, itemId, data) => {
    const response = await axiosInstance.put(`/api/crm/opportunities/${opportunityId}/products/${itemId}`, data);
    return response.data;
  },

  removeProduct: async (opportunityId, itemId) => {
    const response = await axiosInstance.delete(`/api/crm/opportunities/${opportunityId}/products/${itemId}`);
    return response.data;
  }
};

// ==================== PRODUCTS ====================

export const productService = {
  // List products
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/products', { params });
    return response.data;
  },

  // Get product categories
  getCategories: async () => {
    const response = await axiosInstance.get('/api/crm/products/categories');
    return response.data;
  },

  // Get single product
  getById: async (id) => {
    const response = await axiosInstance.get(`/api/crm/products/${id}`);
    return response.data;
  },

  // Create product
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/products', data);
    return response.data;
  },

  // Update product
  update: async (id, data) => {
    const response = await axiosInstance.put(`/api/crm/products/${id}`, data);
    return response.data;
  },

  // Delete (deactivate) product
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/crm/products/${id}`);
    return response.data;
  },

  // Category Management
  getCategories: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/products/categories', { params });
    return response.data;
  },

  createCategory: async (data) => {
    const response = await axiosInstance.post('/api/crm/products/categories', data);
    return response.data;
  },

  updateCategory: async (categoryId, data) => {
    const response = await axiosInstance.put(`/api/crm/products/categories/${categoryId}`, data);
    return response.data;
  },

  deleteCategory: async (categoryId) => {
    const response = await axiosInstance.delete(`/api/crm/products/categories/${categoryId}`);
    return response.data;
  },

  // Product Bundles
  getBundleDetails: async (productId) => {
    const response = await axiosInstance.get(`/api/crm/products/${productId}/bundle`);
    return response.data;
  },

  addBundleItem: async (productId, data) => {
    const response = await axiosInstance.post(`/api/crm/products/${productId}/bundle-items`, data);
    return response.data;
  },

  updateBundleItem: async (productId, itemId, data) => {
    const response = await axiosInstance.put(`/api/crm/products/${productId}/bundle-items/${itemId}`, data);
    return response.data;
  },

  removeBundleItem: async (productId, itemId) => {
    const response = await axiosInstance.delete(`/api/crm/products/${productId}/bundle-items/${itemId}`);
    return response.data;
  }

};

// ==================== PRICE BOOKS ====================

export const priceBookService = {
  // List price books
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/price-books', { params });
    return response.data;
  },

  // Get single price book with entries
  getById: async (id) => {
    const response = await axiosInstance.get(`/api/crm/price-books/${id}`);
    return response.data;
  },

  // Create price book
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/price-books', data);
    return response.data;
  },

  // Update price book
  update: async (id, data) => {
    const response = await axiosInstance.put(`/api/crm/price-books/${id}`, data);
    return response.data;
  },

  // Delete price book
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/crm/price-books/${id}`);
    return response.data;
  },

  // Price Book Entries
  addEntry: async (priceBookId, data) => {
    const response = await axiosInstance.post(`/api/crm/price-books/${priceBookId}/entries`, data);
    return response.data;
  },

  updateEntry: async (priceBookId, entryId, data) => {
    const response = await axiosInstance.put(`/api/crm/price-books/${priceBookId}/entries/${entryId}`, data);
    return response.data;
  },

  removeEntry: async (priceBookId, entryId) => {
    const response = await axiosInstance.delete(`/api/crm/price-books/${priceBookId}/entries/${entryId}`);
    return response.data;
  },

  // Pricing Tiers
  addTier: async (entryId, data) => {
    const response = await axiosInstance.post(`/api/crm/price-books/entries/${entryId}/tiers`, data);
    return response.data;
  },

  updateTier: async (entryId, tierId, data) => {
    const response = await axiosInstance.put(`/api/crm/price-books/entries/${entryId}/tiers/${tierId}`, data);
    return response.data;
  },

  removeTier: async (entryId, tierId) => {
    const response = await axiosInstance.delete(`/api/crm/price-books/entries/${entryId}/tiers/${tierId}`);
    return response.data;
  },

  // Get price for quantity (tiered pricing lookup)
  getPriceForQuantity: async (entryId, quantity) => {
    const response = await axiosInstance.get(`/api/crm/price-books/entries/${entryId}/price`, {
      params: { quantity }
    });
    return response.data;
  }
};

// ==================== QUOTES ====================

export const quoteService = {
  // List quotes
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/quotes', { params });
    return response.data;
  },

  // Get single quote
  getById: async (id) => {
    const response = await axiosInstance.get(`/api/crm/quotes/${id}`);
    return response.data;
  },

  // Create quote
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/quotes', data);
    return response.data;
  },

  // Update quote
  update: async (id, data) => {
    const response = await axiosInstance.put(`/api/crm/quotes/${id}`, data);
    return response.data;
  },

  // Update quote status
  updateStatus: async (id, statusData) => {
    const response = await axiosInstance.put(`/api/crm/quotes/${id}/status`, statusData);
    return response.data;
  },

  // Delete quote
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/crm/quotes/${id}`);
    return response.data;
  },

  // Sync quote to opportunity
  syncToOpportunity: async (id) => {
    const response = await axiosInstance.post(`/api/crm/quotes/${id}/sync`);
    return response.data;
  },

  // Line Items
  addLineItem: async (quoteId, data) => {
    const response = await axiosInstance.post(`/api/crm/quotes/${quoteId}/items`, data);
    return response.data;
  },

  updateLineItem: async (quoteId, itemId, data) => {
    const response = await axiosInstance.put(`/api/crm/quotes/${quoteId}/items/${itemId}`, data);
    return response.data;
  },

  removeLineItem: async (quoteId, itemId) => {
    const response = await axiosInstance.delete(`/api/crm/quotes/${quoteId}/items/${itemId}`);
    return response.data;
  },

  // Generate PDF
  generatePDF: async (id) => {
    const response = await axiosInstance.get(`/api/crm/quotes/${id}/pdf`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    return response.data;
  },

  // Approve quote (for quotes in PENDING_APPROVAL status)
  approve: async (id, data = {}) => {
    const response = await axiosInstance.post(`/api/crm/quotes/${id}/approve`, data);
    return response.data;
  },

  // Reject quote (for quotes in PENDING_APPROVAL status)
  reject: async (id, data) => {
    const response = await axiosInstance.post(`/api/crm/quotes/${id}/reject`, data);
    return response.data;
  }
};

// ==================== FORECASTING ====================

export const forecastService = {
  // Get forecast summary
  getSummary: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/forecast/summary', { params });
    return response.data;
  },

  // Get forecast by period
  getByPeriod: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/forecast/by-period', { params });
    return response.data;
  },

  // Get forecast by category
  getByCategory: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/forecast/by-category', { params });
    return response.data;
  }
};

// ==================== SALES TARGETS ====================

export const salesTargetService = {
  // List targets
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/targets', { params });
    return response.data;
  },

  // Create target
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/targets', data);
    return response.data;
  },

  // Update target
  update: async (id, data) => {
    const response = await axiosInstance.put(`/api/crm/targets/${id}`, data);
    return response.data;
  },

  // Delete target
  delete: async (id) => {
    const response = await axiosInstance.delete(`/api/crm/targets/${id}`);
    return response.data;
  },

  // Recalculate actuals
  recalculateActuals: async () => {
    const response = await axiosInstance.post('/api/crm/targets/recalculate');
    return response.data;
  }

  
};


// ==================== SALES TEAMS ====================

export const salesTeamService = {
  // Get all teams with pagination
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/teams', { params });
    return response.data;
  },

  // Get single team with members
  getById: async (teamId) => {
    const response = await axiosInstance.get(`/api/crm/teams/${teamId}`);
    return response.data;
  },

  // Create team
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/teams', data);
    return response.data;
  },

  // Update team
  update: async (teamId, data) => {
    const response = await axiosInstance.put(`/api/crm/teams/${teamId}`, data);
    return response.data;
  },

  // Delete team
  delete: async (teamId) => {
    const response = await axiosInstance.delete(`/api/crm/teams/${teamId}`);
    return response.data;
  },

  // Get team members
  getMembers: async (teamId, params = {}) => {
    const response = await axiosInstance.get(`/api/crm/teams/${teamId}/members`, { params });
    return response.data;
  },

  // Add team member
  addMember: async (teamId, data) => {
    const response = await axiosInstance.post(`/api/crm/teams/${teamId}/members`, data);
    return response.data;
  },

  // Update member role
  updateMember: async (teamId, memberId, data) => {
    const response = await axiosInstance.put(`/api/crm/teams/${teamId}/members/${memberId}`, data);
    return response.data;
  },

  // Remove member
  removeMember: async (teamId, memberId) => {
    const response = await axiosInstance.delete(`/api/crm/teams/${teamId}/members/${memberId}`);
    return response.data;
  },

  // Get my teams (where I'm a member or manager)
  getMyTeams: async () => {
    const response = await axiosInstance.get('/api/crm/teams/my-teams');
    return response.data;
  },

  getAvailableManagers: async () => {
    const response = await axiosInstance.get('/api/crm/teams/available-managers');
    return response.data;
  },
};


// ==================== ANALYTICS ====================

export const analyticsService = {
  // Get lost reason analytics
  getLostReasonAnalytics: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/analytics/lost-reasons', { params });
    return response.data;
  },
};

// ==================== OPPORTUNITY CONTACTS ====================

export const opportunityContactService = {
  // Get all contacts for an opportunity
  getAll: async (opportunityId) => {
    const response = await axiosInstance.get(`/api/crm/opportunities/${opportunityId}/contacts`);
    return response.data;
  },

  // Add contact to opportunity
  add: async (opportunityId, data) => {
    const response = await axiosInstance.post(`/api/crm/opportunities/${opportunityId}/contacts`, data);
    return response.data;
  },

  // Update contact role on opportunity
  update: async (opportunityId, contactId, data) => {
    const response = await axiosInstance.put(`/api/crm/opportunities/${opportunityId}/contacts/${contactId}`, data);
    return response.data;
  },

  // Remove contact from opportunity
  remove: async (opportunityId, contactId) => {
    const response = await axiosInstance.delete(`/api/crm/opportunities/${opportunityId}/contacts/${contactId}`);
    return response.data;
  },
};

// ==================== SFA SETTINGS ====================

export const sfaSettingsService = {
  // Get SFA settings (discount approval threshold, etc.)
  get: async () => {
    const response = await axiosInstance.get('/api/crm/settings/sfa');
    return response.data;
  },

  // Update SFA settings
  update: async (data) => {
    const response = await axiosInstance.put('/api/crm/settings/sfa', data);
    return response.data;
  }
};

// ==================== TERRITORIES ====================

export const territoryService = {
  // Get all territories
  getAll: async (params = {}) => {
    const response = await axiosInstance.get('/api/crm/territories', { params });
    return response.data;
  },

  // Get single territory with regions
  getById: async (territoryId) => {
    const response = await axiosInstance.get(`/api/crm/territories/${territoryId}`);
    return response.data;
  },

  // Create territory
  create: async (data) => {
    const response = await axiosInstance.post('/api/crm/territories', data);
    return response.data;
  },

  // Update territory
  update: async (territoryId, data) => {
    const response = await axiosInstance.put(`/api/crm/territories/${territoryId}`, data);
    return response.data;
  },

  // Delete territory
  delete: async (territoryId) => {
    const response = await axiosInstance.delete(`/api/crm/territories/${territoryId}`);
    return response.data;
  },

  // Add region to territory
  addRegion: async (territoryId, data) => {
    const response = await axiosInstance.post(`/api/crm/territories/${territoryId}/regions`, data);
    return response.data;
  },

  // Remove region from territory
  removeRegion: async (territoryId, regionId) => {
    const response = await axiosInstance.delete(`/api/crm/territories/${territoryId}/regions/${regionId}`);
    return response.data;
  }
};

// Export all services
export default {
  opportunity: opportunityService,
  product: productService,
  priceBook: priceBookService,
  quote: quoteService,
  forecast: forecastService,
  salesTarget: salesTargetService,
  salesTeam: salesTeamService,
  analytics: analyticsService,
  opportunityContact: opportunityContactService,
  sfaSettings: sfaSettingsService,
  territory: territoryService
};